import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/home',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'Redirect',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/home',
    component: Layout,
    redirect: '/home/welcome',
    name: 'Home',
    meta: {
      title: '首页'
    },
    children: [
      {
        path: 'welcome',
        name: 'Welcome',
        component: () => import('@/views/Home/Welcome.vue'),
        meta: {
          title: t('router.dashboard'),
          // title: '首页',
          icon: 'carbon:home'
        }
      }
    ]
  },
  {
    path: '/notify',
    component: Layout,
    redirect: '/notify/notifyList',
    name: 'Notify',
    meta: {
      title: '通知列表',
      icon: 'carbon:notification'
    },
    children: [
      {
        path: 'notifyList',
        name: 'NotifyList',
        component: () => import('@/views/notify/notifyList.vue'),
        meta: {
          title: '通知列表',
          icon: 'carbon:notification'
        }
      }
    ]
  },
  {
    path: '/service',
    component: Layout,
    redirect: '/service/serviceList',
    name: 'Service',
    meta: {
      title: t('router.serviceManagement'),
      alwaysShow: true,
      icon: 'carbon:align-box-top-left'
    },
    children: [
      {
        path: 'serviceList',
        name: 'ServiceList',
        component: () => import('@/views/service/serviceList.vue'),
        meta: {
          title: t('router.serviceList')
        }
      },
      {
        path: 'contractList',
        name: 'ContractList',
        component: () => import('@/views/service/contractList.vue'),
        meta: {
          title: t('router.contractList')
        }
      },
      {
        path: 'contractProjectManagement',
        name: 'ContractProjectManagement',
        component: () => import('@/views/service/contractProjectManagement.vue'),
        meta: {
          title: '化验项目管理'
          // 合约项目管理
        }
      },
      {
        path: 'contractProjectScene',
        name: 'ContractProjectScene',
        component: () => import('@/views/service/contractProjectScene.vue'),
        meta: {
          title: '化验项目场所管理'
        }
      },
      {
        path: 'auditList',
        name: 'AuditList',
        component: () => import('@/views/service/AuditList.vue'),
        meta: {
          title: '审核列表'
        }
      },
      {
        path: 'reportStatus',
        name: 'ReportStatus',
        component: () => import('@/views/service/reportStatus.vue'),
        meta: {
          title: '报告状态列表'
        }
      },
      {
        path: 'sampleSite',
        name: 'SampleSite',
        component: () => import('@/views/service/sampleSite.vue'),
        meta: {
          title: '取样地址管理'
        }
      }
    ]
  },
  {
    path: '/customerManagement',
    component: Layout,
    redirect: '/customerManagement/customerList',
    name: 'CustomerManagement',
    meta: {
      title: '客户管理',
      icon: 'carbon:user-profile',
      alwaysShow: true
    },
    children: [
      {
        path: 'customerList',
        name: 'CustomerList',
        component: () => import('@/views/customerManagement/customerList.vue'),
        meta: {
          title: '客户列表'
        }
      }
    ]
  },
  {
    path: '/externalLaboratoryManagement',
    component: Layout,
    redirect: '/externalLaboratoryManagement/laboratories',
    name: 'ExternalLaboratoryManagement',
    meta: {
      title: '外部实验室管理',
      icon: 'carbon:chemistry',
      alwaysShow: true
    },
    children: [
      {
        path: 'laboratories',
        name: 'Laboratories',
        component: () => import('@/views/externalLaboratoryManagement/laboratories.vue'),
        meta: {
          title: '外部实验室列表'
        }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    redirect: '/system/nodeSetting',
    name: 'System',
    meta: {
      title: t('router.systemSetting'),
      icon: 'carbon:settings'
    },
    children: [
      {
        path: 'nodeSetting',
        name: 'NodeSetting',
        component: () => import('@/views/system/nodeSetting.vue'),
        meta: {
          title: t('router.nodeSetting')
        }
      },
      {
        path: 'menuSetting',
        name: 'MenuSetting',
        component: () => import('@/views/system/menuSetting.vue'),
        meta: {
          title: t('router.menuSetting')
        }
      },
      {
        path: 'roleManagement',
        name: 'RoleManagement',
        component: () => import('@/views/system/roleManagement.vue'),
        meta: {
          title: t('router.roleManagement')
        }
      },
      {
        path: 'adminManagement',
        name: 'AdminManagement',
        component: () => import('@/views/system/adminManagement.vue'),
        meta: {
          title: t('router.admin')
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  const resetWhiteNameList = ['Redirect', 'Login', 'NoFind', 'Root']
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !resetWhiteNameList.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
